/**
 * P3 §2 requests in plain words, plus the small shared pieces a panel uses to make one:
 * a button that reads "… when online" while the device is offline. How a request is doing shows
 * in the shell's activity tray, never next to the button.
 */
import { Button, cx, type ButtonProps } from "@uni-kit/ui";
import { Loader } from "lucide-react";
import { useCallback, useState } from "react";
import type { RequestState, TetherRequest } from "./api";
import { useDeviceServices, type RequestOptions } from "./services";

/** Collections `data.fetch` can fetch (contracts collections.ts FETCH_COLLS). */
export const FETCH_COLLS = ["contacts", "calls", "sms", "notifications", "location", "wifi", "media", "apps"] as const;
export type FetchColl = (typeof FETCH_COLLS)[number];
export const isFetchColl = (c: string): c is FetchColl => (FETCH_COLLS as readonly string[]).includes(c);

export const COLL_WORDS: Readonly<Record<FetchColl, string>> = {
  contacts: "Contacts", calls: "Call log", sms: "Messages", notifications: "Notifications",
  location: "Location", wifi: "Wi-Fi", media: "Media", apps: "Apps",
};

const ACTIVE: readonly RequestState[] = ["waiting", "queued", "sent", "running"];
/** Not finished yet: it can still be cancelled. */
export const isActiveRequest = (r: { readonly state: RequestState }) => ACTIVE.includes(r.state);
/** Ended without a result: it can be retried. `no_answer`: the phone got it (twice) and didn't answer in time. */
export const isRetryable = (r: { readonly state: RequestState }) => r.state === "failed" || r.state === "expired" || r.state === "cancelled" || isNoAnswer(r);
export const isNoAnswer = (r: { readonly state: RequestState }) => r.state === "no_answer";
/** Made by the server's auto-refresh, not by a person (`origin: "auto"`). */
export const isAutoRequest = (r: Pick<TetherRequest, "origin">) => r.origin === "auto";
/** When it last changed state (best known). */
export const requestTime = (r: TetherRequest): string => r.done_at ?? r.sent_at ?? r.created_at;

const obj = (v: unknown): Record<string, unknown> => (typeof v === "object" && v !== null ? (v as Record<string, unknown>) : {});
const s = (v: unknown): string | null => (typeof v === "string" && v.length > 0 ? v : null);

/** The collections a data.fetch asks for. */
export function fetchColls(r: Pick<TetherRequest, "args">): string[] {
  const c = obj(r.args).colls;
  return Array.isArray(c) ? c.filter((x): x is string => typeof x === "string") : [];
}

/** The file or folder a request is about (args.path / args.root). */
export const requestPath = (r: Pick<TetherRequest, "args">): string | null => s(obj(r.args).path) ?? s(obj(r.args).root);
export const baseName = (path: string): string => path.slice(path.replace(/\/+$/, "").lastIndexOf("/") + 1) || path;

const collWords = (cs: readonly string[]) => cs.map((c) => (isFetchColl(c) ? COLL_WORDS[c] : c)).join(", ");

/** P5 §3 management commands (and the P1 ones the Actions menu sends as requests). */
const P5_TITLES: Readonly<Record<string, string>> = {
  "screen.shot": "Screenshot", "device.lost": "Lost mode", "device.reboot": "Restart", "device.passcode": "Passcode",
  "device.set_time": "Set time", "apps.clear_data": "Clear app data", "device.bugreport": "Bug report",
  "device.deprovision": "Deprovision", "device.wipe": "Wipe", "device.lock": "Lock", "device.ring": "Ring",
  "device.admin.remove": "Turn off lock permission", "device.vibrate": "Vibrate", "device.message": "Message",
  "location.refresh": "Refresh location", "wifi.scan": "Wi-Fi scan", "wifi.set_enabled": "Wi-Fi on/off", "wifi.suggest": "Suggest Wi-Fi",
  "app.launch": "Open app", "app.open_settings": "App settings", "update.check_now": "Check for update", "holder.ask": "Ask the holder",
};

/** "Fetch contacts", "Download", "Fetch folder tree": what was asked, in plain words. */
export function requestTitle(r: Pick<TetherRequest, "kind" | "args">): string {
  switch (r.kind) {
    case "data.fetch": {
      const w = collWords(fetchColls(r));
      return w ? `Fetch ${w.toLowerCase()}` : "Fetch data";
    }
    case "fs.tree": return "Fetch folder tree";
    case "fs.list": return "Open folder";
    case "fs.search": return "Search files";
    case "fs.thumb": return "Thumbnail";
    case "fs.preview": return "Preview";
    case "fs.get": return "Download";
    default: return P5_TITLES[r.kind] ?? r.kind;
  }
}

/** The label or the path's last part: "IMG_2041.jpg". */
export function requestSubject(r: Pick<TetherRequest, "kind" | "args" | "label">): string | null {
  if (r.label) return r.label;
  const p = requestPath(r);
  return p ? (r.kind === "fs.tree" ? p : baseName(p)) : null;
}

/** "Download ready: IMG_2041.jpg", "Contacts fetched": a finished request in one line. */
export function requestDoneText(r: Pick<TetherRequest, "kind" | "args" | "label">): string {
  const subject = requestSubject(r);
  switch (r.kind) {
    case "fs.get": return `Download ready${subject ? `: ${subject}` : ""}`;
    case "fs.thumb":
    case "fs.preview": return `Preview ready${subject ? `: ${subject}` : ""}`;
    case "fs.tree": return "Folder tree fetched";
    case "data.fetch": return `${collWords(fetchColls(r)) || "Data"} fetched`;
    case "screen.shot": return "Screenshot ready";
    case "device.bugreport": return "Bug report ready";
    default: return `${requestTitle(r)} done`;
  }
}

/** A spinning icon for a button whose create call is in flight (the progress itself shows in the activity tray). */
export function Spinner({ className }: { className?: string; "aria-hidden"?: boolean }) {
  return <Loader className={cx("animate-spin", className)} aria-hidden />;
}

export interface RequestButtonProps {
  /** "Fetch", "Download": " when online" is added while the device is offline. */
  readonly label: string;
  readonly online: boolean;
  /** The matching request that hasn't finished: the button is disabled (its progress is in the tray). */
  readonly active?: Pick<TetherRequest, "state"> | undefined;
  /** The create call is in flight: a spinner, disabled. */
  readonly busy?: boolean;
  readonly icon?: ButtonProps["icon"];
  readonly variant?: ButtonProps["variant"];
  readonly onClick: () => void;
}

export function RequestButton({ label, online, active, busy, icon, variant, onClick }: RequestButtonProps) {
  return (
    <Button size="sm" icon={busy ? Spinner : icon} variant={variant ?? "secondary"} aria-busy={busy === true || undefined} disabled={busy === true || active !== undefined} onClick={onClick}>
      {online ? label : `${label} when online`}
    </Button>
  );
}

/**
 * The latest request on this device that `match` accepts, and a way to make one.
 * `match` should be stable or cheap; it runs over the device's request list on every render.
 */
export function useDeviceRequest(match: (r: TetherRequest) => boolean) {
  const { requests } = useDeviceServices();
  const list = requests.useList();
  const latest = (list.data ?? []).find(match);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const start = useCallback(
    async (kind: string, args: unknown, opts?: RequestOptions): Promise<TetherRequest | null> => {
      setBusy(true);
      setError(null);
      try {
        return await requests.create(kind, args, opts);
      } catch (e) {
        setError(e instanceof Error ? e.message : "The request couldn't be made.");
        return null;
      } finally {
        setBusy(false);
      }
    },
    [requests],
  );
  return { latest, active: latest && isActiveRequest(latest) ? latest : undefined, loaded: list.data !== undefined, start, busy, error } as const;
}
